import React from 'react';
import { createUseStyles } from 'react-jss';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Theme, useMediaQuery, useTheme } from '@mui/material';
import { mediaQuery } from '../../../theme';
import CloseIcon from '@mui/icons-material/Close';

const useStyles = createUseStyles((theme: Theme) => {
    const { color, radius, mq } = theme.custom;
    return {
        title: { display: 'flex', alignItems: 'flex-start', gap: 12, paddingRight: 56 },
        close: { position: 'absolute', top: 14, right: 14, color: color.text.secondary },
        description: { margin: '0 0 12px', fontSize: 14, color: color.text.secondary, textWrap: 'pretty' },
        actions: {
            [mq.xs]: {
                flexDirection: 'column-reverse',
                alignItems: 'stretch',
                '& > *': { marginLeft: '0 !important', width: '100%' },
            },
        },
        pending: {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: color.overlay,
            borderRadius: radius.xl,
            zIndex: 2,
        },
    };
});

export interface AppDialogProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    /** One short sentence under the title explaining the consequence of the action. */
    description?: React.ReactNode;
    children?: React.ReactNode;
    /** Buttons, right-aligned; the primary action goes last. */
    actions?: React.ReactNode;
    /** A request is in flight: shows a spinner over the dialog and blocks closing. */
    pending?: boolean;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
    /** Keep the dialog as a sheet on phones instead of going full screen. */
    keepWindowOnMobile?: boolean;
}

/**
 * The one popup shell of the app: MUI Dialog (focus trap, Escape, scroll lock) with the design
 * system's header, close button, pending overlay and phone layout (full screen under 480px).
 */
const AppDialog = ({
    open, onClose, title, description, children, actions, pending = false, maxWidth = 'sm', keepWindowOnMobile = false,
}: AppDialogProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const phone = useMediaQuery(mediaQuery(theme.custom.mq.xs));
    const baseId = React.useId();
    const ids = { title: `${baseId}-title`, description: `${baseId}-description` };
    const close = () => {
        if (!pending) onClose();
    };
    return (
        <Dialog
            open={open}
            onClose={close}
            fullScreen={phone && !keepWindowOnMobile}
            fullWidth
            maxWidth={maxWidth}
            aria-labelledby={ids.title}
            aria-describedby={description ? ids.description : undefined}
            aria-busy={pending || undefined}
            PaperProps={{ sx: phone && !keepWindowOnMobile ? { borderRadius: 0, border: 'none' } : undefined }}
        >
            {pending && (
                <div className={classes.pending} role="status" aria-label="Please wait">
                    <CircularProgress size={44} />
                </div>
            )}
            <DialogTitle id={ids.title} className={classes.title}>{title}</DialogTitle>
            <IconButton aria-label="Close" onClick={close} className={classes.close} size="small" disabled={pending}>
                <CloseIcon fontSize="small" />
            </IconButton>
            <DialogContent>
                {description && <p id={ids.description} className={classes.description}>{description}</p>}
                {children}
            </DialogContent>
            {actions && <DialogActions className={classes.actions}>{actions}</DialogActions>}
        </Dialog>
    );
};

export default AppDialog;
